import { Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useParams } from "react-router";
import { OneOnOneMeetingListItem } from ".";
import { MeetingListItemSkeleton } from "../../../../components";
import { UserMeetingsFilterByOption } from "../../../../constants";
import { OneOnOneMeetingDto, useGetOneOnOnesForUser } from "../../../../http";
import { useUserMeetingsSettings } from "../hooks/useUserMeetingsSettings";

interface IParams {
  userId: string;
}

const OneOnOneMeetingsList = () => {
  const { userId: userGuid } = useParams<keyof IParams>() as IParams;
  const { data, isLoading } = useGetOneOnOnesForUser(userGuid);
  const { settings } = useUserMeetingsSettings(userGuid);

  const filteredMeetings: OneOnOneMeetingDto[] = useMemo(
    () =>
      data?.meetings.filter((meeting) => {
        switch (settings.filterValue) {
          case undefined:
            return true;
          case UserMeetingsFilterByOption.AsLead:
            return meeting.leadUser.userId === userGuid;
          case UserMeetingsFilterByOption.WithLead:
            return meeting.otherUser.userId === userGuid;
          default:
            return false;
        }
      }) ?? [],
    [data, settings, userGuid],
  );

  function meetingSortFunction(a: OneOnOneMeetingDto, b: OneOnOneMeetingDto) {
    if ((a.leadUser.userId === userGuid) !== (b.leadUser.userId === userGuid)) {
      return a.leadUser.userId === userGuid ? 1 : -1;
    }

    const aDate = a.lastInstanceDateTimeUtc ? DateTime.fromISO(a.lastInstanceDateTimeUtc) : null;
    const bDate = b.lastInstanceDateTimeUtc ? DateTime.fromISO(b.lastInstanceDateTimeUtc) : null;

    if (aDate == null && bDate == null) {
      return 0;
    }
    if (aDate == null) {
      return 1;
    }
    if (bDate == null) {
      return -1;
    }

    return bDate < aDate ? -1 : 1;
  }

  return (
    <Stack spacing={1}>
      {isLoading ? (
        <MeetingListItemSkeleton />
      ) : data === undefined ? (
        <Typography sx={{ m: 1 }}>Nothing here yet.</Typography>
      ) : filteredMeetings.length > 0 ? (
        filteredMeetings
          .sort(meetingSortFunction)
          .map((meeting) => <OneOnOneMeetingListItem key={meeting.guid} meeting={meeting} />)
      ) : (
        <Typography sx={{ m: 1 }}>No matches found.</Typography>
      )}
    </Stack>
  );
};

export { OneOnOneMeetingsList };
